import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success(t("auth.welcomeToast", { name: r.user.name }));
      const dest = loc.state?.from?.pathname || "/dashboard";
      nav(dest, { replace: true });
    } else {
      toast.error(r.error);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-primary mb-3">{t("auth.signIn").toUpperCase()}</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-secondary leading-tight">{t("auth.welcomeBack")}</h1>
        <p className="mt-2 text-muted-foreground">{t("auth.accessPortal")}</p>

        <form onSubmit={submit} className="mt-10 space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-primary hover:underline">{t("auth.forgot")}</Link>
            </div>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11">{busy ? t("auth.signingIn") : t("auth.signIn")}</Button>
        </form>

        <div className="relative my-8"><div className="border-t border-border" /><span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-background px-3 text-xs text-muted-foreground">{t("auth.or")}</span></div>

        <Button data-testid="google-signin" onClick={googleSignIn} variant="outline" className="w-full h-11">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {t("auth.continueGoogle")}
        </Button>

        <p className="mt-8 text-sm text-center text-muted-foreground">
          {t("auth.noAccount")} <Link to="/register" data-testid="go-register" className="text-primary font-medium hover:underline">{t("auth.createOne")}</Link>
        </p>
      </section>
    </div>
  );
}
